import React, { useState, useEffect } from 'react';
import { Save, X, Eye, Copy, Check, AlertCircle, MessageSquare } from 'lucide-react';
import { api, getApiBaseUrl } from '../services/api';
import { authService } from '../services/authService';
import { AdminLoading } from './admin/AdminLoading';
import { AdminPageHeader } from './admin/AdminPageHeader';
import { AdminAddButton } from './admin/AdminAddButton';
import { useConfirmDialog } from './admin/ConfirmDialog';
import { AdminActionButton, AdminRowActions } from './admin/AdminActionButton';

const SmsButtonManagement = () => {
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { confirm, confirmDialog } = useConfirmDialog();
  const [showForm, setShowForm] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [copiedButtonIds, setCopiedButtonIds] = useState({});
  const [selectedButtonHtml, setSelectedButtonHtml] = useState('');
  const [selectedButton, setSelectedButton] = useState(null);
  const [loadingButtonId, setLoadingButtonId] = useState(null);
  
  const [formData, setFormData] = useState({
    buttonText: '',
    buttonBodyText: '',
    buttonColor: '#25D366',
    buttonTextColor: '#FFFFFF',
    active: true
  });

  useEffect(() => {
    loadStores();
  }, []);

  useEffect(() => {
    if (selectedStore) {
      loadConfigs(selectedStore.id);
    }
  }, [selectedStore]);

  const loadStores = async () => {
    setError('');
    try {
      const response = await api.get('/admin/stores');
      setStores(response.data);
      if (response.data.length > 0) {
        setSelectedStore(response.data[0]);
      }
    } catch (error) {
      console.error('Error loading stores:', error);
      setError('Failed to load stores');
    } finally {
      setLoading(false);
    }
  };

  const loadConfigs = async (storeId) => {
    setError('');
    try {
      const response = await api.get(`/admin/store-sms-buttons/store/${storeId}`);
      setConfigs(response.data);
    } catch (error) {
      console.error('Error loading configs:', error);
      setError('Failed to load SMS button configs');
    }
  };

  const fallbackCopyTextToClipboard = (text, setCopiedState) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    
    // Avoid scrolling to bottom
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.position = 'fixed';
    
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    setError('');
    try {
      const successful = document.execCommand('copy');
      if (successful) {
        setCopiedState(true);
        setTimeout(() => setCopiedState(false), 2000);
      } else {
        console.error('Fallback: Unable to copy');
      }
    } catch (err) {
      console.error('Fallback: Oops, unable to copy', err);
    }
    
    document.body.removeChild(textArea);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await api.post('/admin/store-sms-buttons', {
        ...formData,
        storeIdFk: selectedStore.id
      });
      await loadConfigs(selectedStore.id);
      setShowForm(false);
      setFormData({
        buttonText: '',
        buttonBodyText: '',
        buttonColor: '#25D366',
        buttonTextColor: '#FFFFFF',
        active: true
      });
    } catch (error) {
      console.error('Error creating config:', error);
      const message = error.response?.data?.error || 'Failed to create SMS button config';
      setError(message);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editingConfig) return;
    setError('');
    try {
      await api.put(`/admin/store-sms-buttons/${editingConfig.id}`, formData);
      await loadConfigs(selectedStore.id);
      setEditingConfig(null);
      setShowForm(false);

      // Refresh preview if the edited button is currently displayed
      if (selectedButton?.id === editingConfig.id) {
        const response = await api.get(`/admin/store-sms-buttons/${editingConfig.id}/embed-html`);
        setSelectedButtonHtml(response.data);
        setSelectedButton(prev => ({ ...prev, ...formData }));
      }
    } catch (error) {
      console.error('Error updating config:', error);
      const message = error.response?.data?.error || 'Failed to update SMS button config';
      setError(message);
    }
  };

  const handleDelete = async (id) => {
    const config = configs.find((c) => c.id === id);
    const label = config?.buttonText ? `"${config.buttonText}"` : 'this button';
    const confirmed = await confirm({
      title: `Delete SMS button ${label}?`,
      message: `The SMS button ${label} will be permanently deleted.`,
      confirmLabel: 'Delete',
    });
    if (!confirmed) return;
    setError('');
    try {
      await api.delete(`/admin/store-sms-buttons/${id}`);
      await loadConfigs(selectedStore.id);
    } catch (error) {
      console.error('Error deleting config:', error);
      const message = error.response?.data?.error || 'Failed to delete SMS button config';
      setError(message);
    }
  };

  const handleViewButtonHtml = async (config) => {
    setLoadingButtonId(config.id);
    setError('');
    try {
      const response = await api.get(`/admin/store-sms-buttons/${config.id}/embed-html`);
      setSelectedButtonHtml(response.data);
      setSelectedButton(config);
    } catch (error) {
      console.error('Error loading button HTML:', error);
      setError('Failed to load button HTML');
    } finally {
      setLoadingButtonId(null);
    }
  };

  const handleCopyPreviewHtml = () => {
    const textToCopy = selectedButtonHtml;

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(textToCopy)
        .then(() => {
          setCopiedButtonIds(prev => ({ ...prev, [selectedButton.id]: true }));
          setTimeout(() => {
            setCopiedButtonIds(prev => ({ ...prev, [selectedButton.id]: false }));
          }, 2000);
        })
        .catch(err => {
          console.error('Failed to copy using clipboard API:', err);
          fallbackCopyTextToClipboard(textToCopy, (copied) => {
            if (copied) {
              setCopiedButtonIds(prev => ({ ...prev, [selectedButton.id]: true }));
              setTimeout(() => {
                setCopiedButtonIds(prev => ({ ...prev, [selectedButton.id]: false }));
              }, 2000);
            }
          });
        });
    } else {
      fallbackCopyTextToClipboard(textToCopy, (copied) => {
        if (copied) {
          setCopiedButtonIds(prev => ({ ...prev, [selectedButton.id]: true }));
          setTimeout(() => {
            setCopiedButtonIds(prev => ({ ...prev, [selectedButton.id]: false }));
          }, 2000);
        }
      });
    }
  };

  const startEdit = (config) => {
    setEditingConfig(config);
    setError('');
    setFormData({
      buttonText: config.buttonText,
      buttonBodyText: config.buttonBodyText || '',
      buttonColor: config.buttonColor,
      buttonTextColor: config.buttonTextColor || '#FFFFFF',
      active: config.active
    });
    setShowForm(true);
  };

  if (loading) return <AdminLoading message="Loading SMS button configurations..." />;

  return (
    <div className="space-y-6">
      <AdminPageHeader
        icon={MessageSquare}
        title="SMS Button Configuration"
        subtitle="Manage store SMS button configurations"
        rightContent={
          <>
            <select
              value={selectedStore?.id || ''}
              onChange={(e) => {
                const store = stores.find(s => s.id === parseInt(e.target.value));
                setSelectedStore(store);
              }}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              {stores.map(store => (
                <option key={store.id} value={store.id}>
                  {store.name || store.storeId}
                </option>
              ))}
            </select>
            {!showForm && (
              <AdminAddButton
                onClick={() => {
                  setShowForm(true);
                  setEditingConfig(null);
                  setFormData({
                    buttonText: '',
                    buttonBodyText: '',
                    buttonColor: '#25D366',
                    buttonTextColor: '#FFFFFF',
                    active: true
                  });
                  setError('');
                }}
                label="Add Button"
              />
            )}
          </>
        }
      />

      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      {showForm && (
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {editingConfig ? 'Edit Button' : 'Add New Button'}
          </h3>
          <form onSubmit={editingConfig ? handleUpdate : handleCreate} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Button Text</label>
                <input
                  type="text"
                  value={formData.buttonText}
                  onChange={(e) => setFormData({ ...formData, buttonText: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm px-3 py-2"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Button Body Text (Optional)</label>
                <input
                  type="text"
                  value={formData.buttonBodyText}
                  onChange={(e) => setFormData({ ...formData, buttonBodyText: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm px-3 py-2"
                  placeholder="e.g., Schedule"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Button Color</label>
                <div className="flex items-center space-x-2 mt-1">
                  <input
                    type="color"
                    value={formData.buttonColor}
                    onChange={(e) => setFormData({ ...formData, buttonColor: e.target.value })}
                    className="h-10 w-20 border border-gray-300 rounded"
                  />
                  <input
                    type="text"
                    value={formData.buttonColor}
                    onChange={(e) => setFormData({ ...formData, buttonColor: e.target.value })}
                    className="flex-1 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm px-3 py-2"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Button Text Color</label>
                <div className="flex items-center space-x-2 mt-1">
                  <input
                    type="color"
                    value={formData.buttonTextColor}
                    onChange={(e) => setFormData({ ...formData, buttonTextColor: e.target.value })}
                    className="h-10 w-20 border border-gray-300 rounded"
                  />
                  <input
                    type="text"
                    value={formData.buttonTextColor}
                    onChange={(e) => setFormData({ ...formData, buttonTextColor: e.target.value })}
                    className="flex-1 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm px-3 py-2"
                  />
                </div>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="active"
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="active" className="ml-2 block text-sm text-gray-900">Active</label>
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingConfig(null);
                  setError('');
                }}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <X className="h-4 w-4 inline mr-1" />
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                <Save className="h-4 w-4 inline mr-1" />
                {editingConfig ? 'Update' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Configs List */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Button Text</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Body Text</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Button Color</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Text Color</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {configs.map((config) => (
              <tr 
                key={config.id} 
                className="hover:bg-gray-50"
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{config.buttonText}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{config.buttonBodyText || '-'}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div
                      className="w-6 h-6 rounded mr-2"
                      style={{ backgroundColor: config.buttonColor }}
                    />
                    <span className="text-sm text-gray-900">{config.buttonColor}</span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div
                      className="w-6 h-6 rounded mr-2 border border-gray-200"
                      style={{ backgroundColor: config.buttonTextColor || '#FFFFFF' }}
                    />
                    <span className="text-sm text-gray-900">{config.buttonTextColor || '#FFFFFF'}</span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    config.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {config.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <AdminRowActions>
                    <AdminActionButton variant="edit" onClick={() => startEdit(config)} />
                    <AdminActionButton variant="info" icon={Eye} label="View HTML" title="View HTML" loading={loadingButtonId === config.id} loadingLabel="Loading…" onClick={() => handleViewButtonHtml(config)} />
                    <AdminActionButton variant="delete" onClick={() => handleDelete(config.id)} />
                  </AdminRowActions>
                </td>
              </tr>
            ))}
            {configs.length === 0 && (
              <tr>
                <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">
                  No SMS buttons configured for this store
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Preview Section */}
      {selectedButton && selectedButtonHtml && (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              Embed HTML for: {selectedButton.buttonText}
            </h3>
            <button
              onClick={() => {
                setSelectedButton(null);
                setSelectedButtonHtml('');
              }}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <div className="text-sm text-gray-600 mb-2">Live Preview:</div>
            <div 
              dangerouslySetInnerHTML={{ __html: selectedButtonHtml }}
            />
          </div>
          <div className="bg-gray-900 rounded-lg p-4 mb-4">
            <pre className="text-sm text-gray-300 overflow-x-auto">
              <code>{selectedButtonHtml}</code>
            </pre>
          </div>
          <div className="flex justify-end">
            <button
              onClick={handleCopyPreviewHtml}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              {copiedButtonIds[selectedButton.id] ? (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4 mr-2" />
                  Copy HTML
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {confirmDialog}
    </div>
  );
};

export default SmsButtonManagement;
